import { useCustom, useGetLocale, useTranslate } from '@refinedev/core';
import { Alert, Button, Input, Modal, Select, Space, Table, Typography } from 'antd';
import { useState } from 'react';
import { publishInstrumentInputSchema } from '@cc/schema';
import {
  DEFINITION_FIELDS,
  bandDraftsOf,
  bandDraftsProblem,
  publishInputOf,
  type BandDraft,
  type Definition,
} from '../assessments/model';
import { useAssessmentsCommand } from '../assessments/useAssessmentsCommand';

type Tr = (k: string, o?: Record<string, unknown>) => string;

/**
 * E35 · PublishInstrument - "Backoffice - Instruments - Publish" (cc-assessments, chapter "Assessments - Instrument catalog (backoffice +
 * mobile, E35)"): the five instruments are seeded with the same fact; this is how a definition is REVISED without a release - a
 * corrected band table re-scores history, because interpretation is read-time. The question TEXT is edited in the i18n catalogue,
 * not here. A new instrument needs its questions and its type, so it ships with a release. Members only (the server's gate).
 */
export const BackofficeInstruments = () => {
  const getLocale = useGetLocale();
  void getLocale;
  const t = useTranslate() as unknown as Tr;
  const { run, isLoading } = useAssessmentsCommand();
  const { data, isFetching, isError, refetch } = useCustom<Definition[]>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: { operation: 'instrumentDefinitions', fields: DEFINITION_FIELDS },
  });
  const [def, setDef] = useState<Definition | null>(null);
  const [name, setName] = useState('');
  const [nameRu, setNameRu] = useState('');
  const [availableFor, setAvailableFor] = useState('public');
  const [citation, setCitation] = useState('');
  const [bands, setBands] = useState<BandDraft[]>([]);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);

  const edit = (d: Definition) => {
    setMessage(null);
    setModalError(null);
    setDef(d);
    setName(d.name);
    setNameRu(d.nameRu ?? '');
    setAvailableFor(d.availableFor);
    setCitation(d.citation);
    setBands(bandDraftsOf(d));
  };

  const setBand = (row: number, i: number, key: 'min' | 'max' | 'band', value: string) =>
    setBands((bs) =>
      bs.map((r, ri) =>
        ri === row ? { ...r, bands: r.bands.map((b, bi) => (bi === i ? { ...b, [key]: value } : b)) } : r,
      ),
    );

  const publish = async () => {
    if (!def) return;
    const input = publishInputOf(def, { name, nameRu, availableFor, citation }, bands);
    try {
      await publishInstrumentInputSchema.validate(input);
    } catch {
      setModalError(t('backoffice.instruments.invalid'));
      return;
    }
    const res = await run('publishInstrument', input, t('backoffice.instruments.failed'));
    if (res.ok) {
      setMessage({ type: 'success', text: t('backoffice.instruments.published') });
      setDef(null);
      refetch();
    } else setModalError(res.message);
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }} data-testid="backoffice-instruments">
      <div>
        <Typography.Title level={3} style={{ marginBottom: 0 }}>
          {t('backoffice.instruments.title')}
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {t('backoffice.instruments.body')}
        </Typography.Paragraph>
      </div>
      {message ? <Alert showIcon type={message.type} message={message.text} data-testid="backoffice-instruments-message" /> : null}
      {isError ? <Alert showIcon type="error" message={t('backoffice.instruments.loadFailed')} /> : null}
      <Table<Definition>
        rowKey="instrumentId"
        loading={isFetching}
        pagination={false}
        dataSource={data?.data ?? []}
        columns={[
          { title: t('backoffice.instruments.col.id'), dataIndex: 'instrumentId' },
          { title: t('backoffice.instruments.col.name'), dataIndex: 'name' },
          { title: t('backoffice.instruments.col.items'), dataIndex: 'itemsCount' },
          { title: t('backoffice.instruments.col.availability'), dataIndex: 'availableFor' },
          {
            title: '',
            key: 'edit',
            render: (_: unknown, d: Definition) => (
              <Button data-testid={`backoffice-instrument-edit-${d.instrumentId}`} onClick={() => edit(d)}>
                {t('backoffice.instruments.revise')}
              </Button>
            ),
          },
        ]}
      />
      <Modal
        open={Boolean(def)}
        title={def ? t('backoffice.instruments.reviseTitle', { name: def.name }) : ''}
        onCancel={() => setDef(null)}
        onOk={publish}
        okText={t('backoffice.instruments.publish')}
        cancelText={t('common.cancel')}
        confirmLoading={isLoading}
        okButtonProps={{ disabled: bandDraftsProblem(bands), 'data-testid': 'backoffice-instrument-publish' } as never}
        width={640}
        destroyOnClose>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('backoffice.instruments.col.name')} />
          <Input value={nameRu} onChange={(e) => setNameRu(e.target.value)} placeholder={t('backoffice.instruments.nameRu')} />
          <Select
            style={{ width: '100%' }}
            value={availableFor}
            onChange={setAvailableFor}
            options={[
              { value: 'public', label: t('backoffice.instruments.availability.public') },
              { value: 'assignedOnly', label: t('backoffice.instruments.availability.assignedOnly') },
            ]}
          />
          <Input.TextArea value={citation} onChange={(e) => setCitation(e.target.value)} placeholder={t('backoffice.instruments.citation')} />
          <Typography.Text strong>{t('backoffice.instruments.bands')}</Typography.Text>
          {bands.map((row, ri) => (
            <div key={ri}>
              <Typography.Text type="secondary">{row.subscale ?? t('assessments.result.total')}</Typography.Text>
              {row.bands.map((b, bi) => (
                <Space key={bi} style={{ display: 'flex' }}>
                  <Input
                    style={{ width: 80 }}
                    data-testid={`backoffice-band-${ri}-${bi}-min`}
                    value={b.min}
                    onChange={(e) => setBand(ri, bi, 'min', e.target.value)}
                  />
                  <Input
                    style={{ width: 80 }}
                    data-testid={`backoffice-band-${ri}-${bi}-max`}
                    value={b.max}
                    onChange={(e) => setBand(ri, bi, 'max', e.target.value)}
                  />
                  <Input
                    data-testid={`backoffice-band-${ri}-${bi}-id`}
                    value={b.band}
                    onChange={(e) => setBand(ri, bi, 'band', e.target.value)}
                  />
                </Space>
              ))}
            </div>
          ))}
          {modalError ? <Alert type="error" showIcon message={modalError} data-testid="backoffice-instruments-error" /> : null}
        </Space>
      </Modal>
    </Space>
  );
};
